import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Mail, Flag, Trash2, CheckCircle2, Eye } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

const FILTERS = [["", "All"], ["new", "New"], ["read", "Read"], ["resolved", "Resolved"]];

export default function AdminMessages() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [topic, setTopic] = useState("");
  const { data } = useQuery({ queryKey: ["admin-messages", status, topic], queryFn: async () => (await api.get("/admin/messages", { params: { status: status || undefined, topic: topic || undefined, limit: 200 } })).data });

  const update = async (id, next) => {
    try { await api.put(`/admin/messages/${id}/status`, { status: next }); qc.invalidateQueries({ queryKey: ["admin-messages"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/admin/messages/${id}`); qc.invalidateQueries({ queryKey: ["admin-messages"] }); toast.success("Message deleted."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-messages">
      <section className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-foreground">Messages</h2>
            <p className="mt-1 text-sm text-muted-foreground">From the Contact and Report-a-Correction forms.{data ? ` ${data.unread} unread.` : ""}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="flex overflow-hidden rounded-full border border-border font-mono text-[10px] uppercase tracking-wider">
              {FILTERS.map(([k, l]) => <button key={k} type="button" onClick={() => setStatus(k)} aria-pressed={status === k} data-testid={`messages-filter-${k || "all"}`} className={`px-3 py-1.5 ${status === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>)}
            </div>
            <div className="flex overflow-hidden rounded-full border border-border font-mono text-[10px] uppercase tracking-wider">
              {[["", "Both"], ["contact", "Contact"], ["correction", "Corrections"]].map(([k, l]) => <button key={k} type="button" onClick={() => setTopic(k)} aria-pressed={topic === k} data-testid={`messages-topic-${k || "all"}`} className={`px-3 py-1.5 ${topic === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>)}
            </div>
          </div>
        </div>
        <div className="mt-5 space-y-3">
          {!data && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
          {data?.messages.length === 0 && <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="messages-empty">No messages here.</p>}
          {data?.messages.map((m) => (
            <article key={m.id} className={`rounded-2xl border p-4 ${m.status === "new" ? "border-gold/50 bg-gold/5" : "border-border"}`} data-testid={`message-${m.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  {m.topic === "correction" ? <Flag className="h-4 w-4 text-gold" /> : <Mail className="h-4 w-4 text-gold" />}
                  <span className="font-semibold text-foreground">{m.name}</span>
                  <a href={`mailto:${m.email}`} className="text-sm text-primary hover:underline">{m.email}</a>
                  <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{m.status}</span>
                </div>
                <span className="font-mono text-[11px] text-muted-foreground">{fmtDateTime(m.created_at)}</span>
              </div>
              {m.reference && <p className="mt-2 text-sm"><span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Where: </span><span className="text-foreground">{m.reference}</span></p>}
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{m.message}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {m.status === "new" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => update(m.id, "read")} data-testid={`message-${m.id}-read`}><Eye className="mr-1.5 h-3.5 w-3.5" /> Mark read</Button>}
                {m.status !== "resolved" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => update(m.id, "resolved")} data-testid={`message-${m.id}-resolve`}><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Resolve</Button>}
                <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => remove(m.id)} data-testid={`message-${m.id}-delete`}><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete</Button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
